import React, { FC, useEffect } from "react";
import { useImmer } from "use-immer";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { ConfigurationDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

import { usePatchConfigurations } from "../../api/configurations/patch-configurations";
import { useGetOrg } from "../../api/organizations/get-organization";
import Loading from "../../utils/Loading";

import { ConfigurationInput } from "./AdminSettings/AdvancedSettings/ConfigurationInputs";
import {
  getNewModifiedConfigs,
  onConfigChange,
  onLockedChange,
  sortSettings,
} from "./Utils/Utils";

const PREFIX = "ScopeSettings";

const classes = {
  container: `${PREFIX}-container`,
  sectionLabel: `${PREFIX}-sectionLabel`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.container}`]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },

  [`& .${classes.sectionLabel}`]: {
    marginTop: 5,
  },
}));

export const ScopeSettings: FC = () => {
  const organization = useGetOrg();
  const [modifiedConfigs, setModifiedConfigs] = useImmer<ConfigurationDto[]>(
    []
  );
  const [settingsState, setSettingsState] = useImmer<ConfigurationDto[]>([]);

  const mutateConfigurations = usePatchConfigurations();

  const keysToFilterIn = [
    "enable_ai",
    "enable_price_csv_download",
    "should_show_profit_in_dashboard",
    "should_show_sitewide_dashboard",
    "edit_currency_filter_for_price_tests",
    "enable_schedule_tests",
    "live_chat_enabled",
    "max_enabled_offers",
    "enable_bundle_products",
    "functions_include_subscription_products",
  ];

  useEffect(() => {
    const newSettingsState = organization.data.org.configurations.filter(
      (config) =>
        config.topic === "WEB_UI_SCOPE" || keysToFilterIn.includes(config.key)
    );

    setSettingsState(sortSettings(newSettingsState));
    setModifiedConfigs(
      getNewModifiedConfigs(
        newSettingsState,
        organization.data.org.configurations
      )
    );
  }, [organization.data.org.configurations]);

  useEffect(() => {
    if (settingsState) {
      setModifiedConfigs(
        getNewModifiedConfigs(
          settingsState,
          organization.data.org.configurations
        )
      );
    }
  }, [organization.data.org.configurations, settingsState]);

  if (settingsState) {
    return (
      <Grid container direction={"column"} rowSpacing={2}>
        <IgTypography className={classes.sectionLabel} variant="h3">
          Organization Permissions
        </IgTypography>

        {sortSettings(settingsState).map(
          (config: ConfigurationDto, _index: number) => (
            <Grid item key={config.key}>
              <ConfigurationInput
                key={config.key}
                config={config}
                onChange={(newValue: any) =>
                  onConfigChange(config.key, newValue, setSettingsState)
                }
                onLockedChange={
                  config.topic === "WEB_UI_SCOPE"
                    ? (locked) => {
                        onLockedChange(config.key, locked, setSettingsState);
                      }
                    : undefined
                }
              />
            </Grid>
          )
        )}

        <Grid item justifyContent={"end"} alignItems={"end"} textAlign="end">
          <IgButton
            loading={mutateConfigurations.isPending}
            style={{ marginTop: 5 }}
            size="medium"
            variant="contained"
            onClick={() => {
              mutateConfigurations.mutate(modifiedConfigs || []);
            }}
          >
            Save Permissions
          </IgButton>
        </Grid>
      </Grid>
    );
  }

  return <Loading />;
};
